import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import BuyChipsModal from "@/components/BuyChipsModal";
import { Trophy, Sparkles, TrendingUp } from "lucide-react";
import { GAMES } from "@/pages/games/registry";

export default function Lobby() {
  const { user, refresh } = useAuth();
  const [buyOpen, setBuyOpen] = useState(false);
  const [daily, setDaily] = useState(null);
  const [wallet, setWallet] = useState({ stats: {}, transactions: [] });
  const [weekendBoost, setWeekendBoost] = useState(false);

  useEffect(() => {
    refresh();
    api.get("/daily-challenge").then((r) => setDaily(r.data.challenge)).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/games/unicorn/config").then((r) => setWeekendBoost(!!r.data.weekend_boost)).catch(() => {});
  }, []); // eslint-disable-line

  const stats = wallet.stats || {};
  const winRate = stats.games_played ? Math.round(((stats.total_won || 0) / Math.max(1, stats.total_wagered || 1)) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Stats bar */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="vexis-card p-4" data-testid="stat-chips">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Chips</div>
          <div className="mt-1 font-display text-2xl text-[color:var(--gold)]">{user?.chips?.toLocaleString?.() ?? 0}</div>
          <button onClick={() => setBuyOpen(true)} className="text-xs mt-2 text-slate-400 hover:text-white underline underline-offset-4 decoration-white/20" data-testid="buy-chips-button">Buy more</button>
        </div>
        <div className="vexis-card p-4" data-testid="stat-played">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Games played</div>
          <div className="mt-1 font-display text-2xl">{stats.games_played || 0}</div>
        </div>
        <div className="vexis-card p-4" data-testid="stat-biggest">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Biggest win</div>
          <div className="mt-1 font-display text-2xl">{(stats.biggest_win || 0).toLocaleString()}</div>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="pill">Skill Games</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">Choose your table</h2>
          </div>
          <Link to="/leaderboard" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2" data-testid="lobby-leaderboard-link">
            <Trophy className="w-4 h-4" /> Leaderboard
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {GAMES.map((g) => (
            <Link to={g.path} key={g.id} data-testid={`game-card-${g.id}`}
              className={`vexis-card vexis-card-hover hover-lift p-6 relative overflow-hidden bg-gradient-to-br ${g.tint} to-transparent`}>
              <g.icon className="w-8 h-8 text-[color:var(--gold)]" />
              <h3 className="font-display text-2xl mt-4">{g.label}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{g.blurb}</p>
              <div className="mt-6 flex items-center justify-between">
                <span className="pill pill-gold">Play</span>
                <span className="font-mono text-xs text-slate-500">min bet · 10</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-4">
        <Link to="/daily" className="vexis-card vexis-card-hover p-6 md:col-span-2 relative overflow-hidden" data-testid="daily-widget">
          <div className="flex items-center gap-2 pill pill-gold w-fit"><Sparkles className="w-3.5 h-3.5" /> Today&apos;s AI Challenge</div>
          <h3 className="font-display text-2xl mt-3">{daily?.title || "Crafting today's mission…"}</h3>
          <p className="text-sm text-slate-400 mt-2 max-w-lg">{daily?.description || "Our curator is preparing a bespoke challenge for you."}</p>
          {daily && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="pill">Game · {daily.game}</span>
              <span className="pill">Reward · {daily.reward} chips</span>
              <span className="pill">Difficulty · {daily.difficulty}</span>
            </div>
          )}
        </Link>
        <div className="vexis-card p-6" data-testid="recent-activity">
          <div className="pill">Recent Activity</div>
          <ul className="mt-3 space-y-2 text-sm">
            {(wallet.transactions || []).slice(0, 5).map((t) => (
              <li key={t._id} className="flex items-center justify-between border-b border-white/5 pb-1.5">
                <span className="text-slate-400 capitalize">{t.type}</span>
                <span className={`font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
              </li>
            ))}
            {(wallet.transactions || []).length === 0 && <li className="text-slate-500 text-sm">No plays yet. Deal a hand.</li>}
          </ul>
        </div>
      </section>

      <BuyChipsModal open={buyOpen} onClose={() => setBuyOpen(false)} />
    </div>
  );
}
